import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { HotWheelFormFields, HotWheelFormValues } from '../../component/hotwheel-form-fields/hotwheel-form-fields';
import { HotWheelImageUploader } from '../../component/hotwheel-image-uploader/hotwheel-image-uploader';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { useInsertHotWheel, useUpdateHotWheel, useGetHotWheel } from '../../hooks/use-hotwheels';
import { HotWheelInsertInput, HotWheelUpdateInput } from '../../types/hotwheels.types';

interface HotWheelsFormProps {
  mode: 'create' | 'edit';
  itemId?: string;
}

export function HotWheelsForm({ mode, itemId }: HotWheelsFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { mutateAsync: insertCar, isPending: isInserting } = useInsertHotWheel();
  const { mutateAsync: updateCar, isPending: isUpdating } = useUpdateHotWheel();
  const { data: existingCar, isLoading: isLoadingCar } = useGetHotWheel(mode === 'edit' ? itemId : undefined);

  const [mainImageId, setMainImageId] = useState<string | null>(null);
  const [existingImagePreview, setExistingImagePreview] = useState<string | null>(null);
  const [flags, setFlags] = useState({
    Favorite: false,
    TreasureHunt: false,
    SuperTreasureHunt: false,
    LimitedEdition: false,
    PublicVisibility: false,
  });

  useEffect(() => {
    if (mode === 'edit' && existingCar) {
      setMainImageId(existingCar.MainImage ?? null);
      setFlags({
        Favorite: existingCar.Favorite,
        TreasureHunt: existingCar.TreasureHunt,
        SuperTreasureHunt: existingCar.SuperTreasureHunt,
        LimitedEdition: existingCar.LimitedEdition,
        PublicVisibility: existingCar.PublicVisibility,
      });
    }
  }, [mode, existingCar]);

  if (mode === 'edit' && isLoadingCar) {
    return (
      <div className="space-y-4 max-w-4xl mx-auto">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-72 w-full rounded-2xl" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const defaultValues: Partial<HotWheelFormValues> =
    mode === 'edit' && existingCar
      ? {
          Brand: existingCar.Brand,
          Series: existingCar.Series,
          ModelNumber: existingCar.ModelNumber,
          ReleaseYear: existingCar.ReleaseYear,
          Color: existingCar.Color ?? '',
          Scale: existingCar.Scale ?? '',
          Condition: existingCar.Condition as HotWheelFormValues['Condition'],
          PurchasePrice: existingCar.PurchasePrice as unknown as HotWheelFormValues['PurchasePrice'],
          EstimatedValue: existingCar.EstimatedValue as unknown as HotWheelFormValues['EstimatedValue'],
          PurchaseDate: existingCar.PurchaseDate?.split('T')[0] ?? '',
          StorageLocation: existingCar.StorageLocation ?? '',
          Description: existingCar.Description ?? '',
        }
      : undefined;

  const handleSubmit = async (values: HotWheelFormValues) => {
    try {
      const numOrUndef = (v: unknown) =>
        v === '' || v === undefined || v === null ? undefined : Number(v);
      const imageId = mainImageId;
      const flagsSnapshot = { ...flags };

      if (mode === 'create') {
        const payload: HotWheelInsertInput = {
          ...values,
          ...flagsSnapshot,
          MainImage: imageId ?? undefined,
          PurchasePrice: numOrUndef(values.PurchasePrice),
          EstimatedValue: numOrUndef(values.EstimatedValue),
        };
        await insertCar(payload);
      } else if (itemId) {
        const payload: HotWheelUpdateInput = {
          ...values,
          ...flagsSnapshot,
          MainImage: imageId ?? undefined,
          PurchasePrice: numOrUndef(values.PurchasePrice),
          EstimatedValue: numOrUndef(values.EstimatedValue),
        };
        await updateCar({ itemId, input: payload });
      }
      navigate('/collection');
    } catch (err) {
      toast({
        title: t('SAVE_FAILED'),
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white">
          {mode === 'create' ? t('ADD_CAR') : t('EDIT_CAR')}
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <HotWheelImageUploader
            value={mainImageId}
            onChange={(fileId, preview) => {
              setMainImageId(fileId);
              setExistingImagePreview(preview);
            }}
            existingPreviewUrl={existingImagePreview}
          />
        </div>

        <div className="space-y-4">
          <HotWheelFormFields
            defaultValues={defaultValues}
            onSubmit={handleSubmit}
            isPending={isInserting || isUpdating}
            submitLabel={t('SAVE')}
            onCancel={() => navigate('/collection')}
          />

          <div className="space-y-2 border-t border-zinc-800 pt-4">
            {(
              [
                ['Favorite', t('FAVORITE')],
                ['TreasureHunt', t('TREASURE_HUNT')],
                ['SuperTreasureHunt', t('SUPER_TREASURE_HUNT')],
                ['LimitedEdition', t('LIMITED_EDITION')],
                ['PublicVisibility', t('PUBLIC_VISIBILITY')],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={flags[key]}
                  onCheckedChange={(checked) =>
                    setFlags((f) => ({ ...f, [key]: Boolean(checked) }))
                  }
                />
                <span className="text-sm text-zinc-200">{label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}